import { Info } from '@mui/icons-material';
import {
  Autocomplete,
  Chip,
  FormControlLabel,
  FormHelperText,
  Link,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import {
  FormDetail,
  FormsErrorDataType,
} from 'containers/dashboard/Forms/FormListingType';
import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

type Props = {
  currencies: Record<string, string>;
  selectedMode: boolean;
  selectedCurrency: string;
  allowDonation: boolean;
  selectedPaymentType: string;
  suggestedAmount: number[];
  amount: number;
  customAmount: number;
  allowCustomAmount: boolean;
  subscriptionEnabled: boolean;
  defaultGateway: string | undefined;
  setTabSelected: React.Dispatch<React.SetStateAction<any>>;
  selectableDefaultGateways: Array<{ name: string; label: string }>;
  onChangeElement: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | SelectChangeEvent
  ) => void;
  onChangeSuggestion: (name: string, value: number[]) => void;
  onChangeMode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  stateError: FormsErrorDataType;
  formData: FormDetail;
};

const PaymentSettingsForm: FunctionComponent<Props> = ({
  allowDonation,
  allowCustomAmount,
  suggestedAmount,
  onChangeSuggestion,
  onChangeElement,
  onChangeMode,
  amount,
  customAmount,
  stateError,
  subscriptionEnabled,
  setTabSelected,
  formData,
}) => {
  const { t } = useTranslation();
  const { products } = useSelector((state: any) => state.productsFromEditor);

  return (
    <>
      {formData.products &&
      formData.products.length === 0 &&
      products &&
      products.length === 0 ? (
        <>
          <Stack direction='column' spacing='30px'>
            {allowDonation ? (
              <FormControlLabel
                control={
                  <Switch
                    name='allowCustomAmount'
                    checked={allowCustomAmount}
                    onChange={onChangeMode}
                  />
                }
                label={t('allow_custom_amount')}
              />
            ) : null}

            {allowCustomAmount && allowDonation ? (
              <>
                <TextField
                  id='outlined-basic'
                  name='customAmount'
                  label={t('custom_amount')}
                  variant='outlined'
                  type='number'
                  value={customAmount}
                  onChange={onChangeElement}
                  error={stateError.customAmount.length ? true : false}
                  helperText={stateError.customAmount}
                />

                <Autocomplete<number, true, false, true>
                  fullWidth
                  onChange={(event, newValue) => {
                    onChangeSuggestion(
                      'suggestedAmount',
                      newValue as unknown as number[]
                    );
                  }}
                  multiple
                  id='tags-filled'
                  options={[]}
                  value={suggestedAmount!}
                  freeSolo
                  renderTags={(value: readonly number[], getTagProps) =>
                    value.map((option: number, index: number) => (
                      <Chip
                        sx={{ backgroundColor: 'white' }}
                        variant='outlined'
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={params => (
                    <TextField
                      type='number'
                      {...params}
                      label={t('suggested_amount')}
                      placeholder={
                        suggestedAmount.length === 0
                          ? t('enter_amount_press_enter')
                          : t('suggested_amount')
                      }
                      error={stateError.suggestedAmount.length ? true : false}
                      helperText={stateError.suggestedAmount}
                    />
                  )}
                />
                <div className='mt-2 flex items-center text-[14px] text-gray-600'>
                  <Info sx={{ width: '15px', height: '15px' }} />{' '}
                  <span className='ml-1'> {t('prefer_multiple_3')}</span>
                </div>
              </>
            ) : (
              <>
                {subscriptionEnabled && (
                  <div className='mb-2 mt-[-5px] flex items-center text-[14px] text-gray-600'>
                    <Info sx={{ width: '15px', height: '15px' }} />{' '}
                    <span className='ml-1'>
                      {' '}
                      {t('subscription_enabled_notice')}
                    </span>
                  </div>
                )}
                <TextField
                  id='outlined-basic'
                  name='amount'
                  label={t('enter_amount')}
                  variant='outlined'
                  type='number'
                  value={amount}
                  onChange={onChangeElement}
                  error={stateError.amount.length ? true : false}
                  helperText={stateError.amount}
                  disabled={subscriptionEnabled}
                />
              </>
            )}

            <Stack direction='row' spacing='10px'>
              {true && (
                <FormHelperText error={true}>
                  {stateError.paymentType}
                </FormHelperText>
              )}
            </Stack>
          </Stack>
        </>
      ) : (
        <>
          <TextField
            id='outlined-basic'
            name='amount'
            label={t('enter_amount')}
            variant='outlined'
            type='number'
            value={amount}
            disabled
            className='w-[100%]'
          />
          <div className=' mt-3 flex items-center text-[14px] text-gray-600'>
            <span className='ml-1'>
              {t('custom_amount_disabled')}{' '}
              <Link
                component='button'
                variant='body2'
                onClick={() => setTabSelected('products')}
              >
                {t('products')}
              </Link>{' '}
              {t('enable')}
            </span>
          </div>
        </>
      )}
    </>
  );
};

export default React.memo(PaymentSettingsForm);
